import useStoreAndLanguageParams from '../../core/hooks/useStoreAndLanguageParams';
import notEmpty from '../../core/util/notEmpty';
import type { GeneralNextAvailabilityStatus } from '../types';
import useProductOrderRestriction from './useProductOrderRestriction';
import useStockQuantity from './useStockQuantityQuery';

const useStockAtLocation = ({
    sku,
    location,
    skipLoadingStocks,
}: {
    sku: string;
    location?: string;
    skipLoadingStocks?: boolean;
}) => {
    const { store } = useStoreAndLanguageParams();
    const { data, error, loading } = useStockQuantity({
        store,
        sku,
        stockLocation: location,
        skipLoadingStocks: skipLoadingStocks,
    });

    const { restriction, loading: loadingRestriction } =
        useProductOrderRestriction({
            sku,
            skip: skipLoadingStocks,
        });

    const defaultStock = {
        location,
        sku,
        store,
        quantity: undefined,
        nextAvailability: 'unknown' as GeneralNextAvailabilityStatus,
        unavailable: true,
    };

    if (error) {
        throw error;
    }

    if (loading || loadingRestriction || skipLoadingStocks) {
        return {
            stock: defaultStock,
            loading: true,
        };
    }

    if (!data || !notEmpty(data.singleStockQuantity.quantity)) {
        return {
            stock: defaultStock,
            loading: false,
        };
    }

    return {
        stock: data.singleStockQuantity,
        futureStock: data.productFutureStock,
        restriction,
        loading,
    };
};

export default useStockAtLocation;
